/**
 * MenuContent component.
 */
import Container from '@components/atoms/container/menuContentContainer';
import Context from '@shared/components/utils/context/menuContext';
import DeviceConfig from '@devices/config';
import Direction from '@shared/types/enums/direction';
import Focus from '@shared/types/enums/focus';
import Item from '@components/elements/menu/menuItem';
import List from '@components/sections/list/list';
import Modals from '@shared/types/enums/modals';
import useKeyboard from '@shared/components/utils/hooks/useKeyboard';
import useUpdateMenu from '@shared/components/utils/hooks/useUpdateMenu';
import type { MenuItem } from '@shared/components/menu/menuData';
import { emptyRoute, routerSelector } from '@shared/store/slices/router.slice';
import { focusSelector, setFocus } from '@shared/store/slices/focus.slice';
import { popRoute } from '@shared/store/thunks/route.thunk';
import { setAds } from '@shared/store/slices/ads.slice';
import { setModal } from '@shared/store/slices/modal.slice';
import { useAppDispatch, useAppSelector } from '@shared/store/hooks';
import { useCallback, useContext, useEffect, useMemo, useRef } from 'react';

/**
 * This component displays a list of navigatable menu items.
 * @param {Props} parentProps - The parent props.
 * @returns {JSX.Element} The list of menu items.
 */
export default function MenuContent() {
  const { callbacks, props, items } = useMenuContent();
  return (
    <Container>
      <List {...props.list} {...callbacks.list}>
        {items.map((item, index) => (
          <Item index={index} key={index} item={item} {...callbacks.item} />
        ))}
      </List>
    </Container>
  );
}

/**
 * Hook to manage the menu content component.
 * @returns {Object} The menu content component.
 */
function useMenuContent() {
  // ------------------------------------------------------------
  // Variables
  // ------------------------------------------------------------

  const dispatch = useAppDispatch();
  const items = useUpdateMenu();

  // Context
  const { collapse, display, effectiveIndex, setListIndex } =
    useContext(Context);

  // Selectors
  const { focus } = useAppSelector(focusSelector);
  const { route, params } = useAppSelector(routerSelector);

  // Refs
  const displayRef = useRef<boolean>(display);
  const focusedRef = useRef<boolean>(false);
  const itemsRef = useRef<MenuItem[]>([]);
  const stackRef = useRef(route.stack);
  displayRef.current = display;
  focusedRef.current = focus === Focus.MENU;
  itemsRef.current = items;
  stackRef.current = route.stack;

  // ------------------------------------------------------------
  // Callbacks
  // ------------------------------------------------------------

  const loadAd = useCallback((index: number) => {
    if (!itemsRef.current.length) return;
    if (index < 0) return;
    const adSource = itemsRef.current[index].ad();
    dispatch(setAds(adSource));
  }, []);

  const loadPage = useCallback((index: number) => {
    if (!itemsRef.current.length) return;
    if (index < 0) return;
    itemsRef.current[index].action();
    effectiveIndex.current = index;
  }, []);

  // ------------------------------------------------------------
  // Event handlers
  // ------------------------------------------------------------

  const onChange = useCallback((index: number) => {
    // If index is less than 0 or no data is available, do nothing
    if (!displayRef.current) return;
    if (index < 0) return;
    if (!itemsRef.current.length) return;
    if (!focusedRef.current) return;

    // Clear route and load new page
    dispatch(emptyRoute());
    loadPage(index);
  }, []);

  const onClick = useCallback((index?: number) => {
    if (!displayRef.current) return;
    if (index === undefined) return;
    if (index < 0) return;
    if (index === effectiveIndex.current && stackRef.current.length === 0) {
      if (itemsRef.current[index].modal) {
        itemsRef.current[index].modal();
      }
      return;
    }
    loadPage(index);
    loadAd(index);
    dispatch(setFocus(Focus.MENU));
    setListIndex.current(index);
  }, []);

  const onFocus = useCallback((index: number) => {
    if (!displayRef.current) return;
    if (index < 0) return;
    if (effectiveIndex.current !== index) {
      loadPage(index);
    }
  }, []);

  const outOfBounds = useCallback(
    (_listFocus: Focus | number | string, key?: string) => {
      if (!displayRef.current) return;
      switch (key) {
        case 'UP':
          dispatch(setFocus(Focus.SEARCH));
          return;
      }
    },
    []
  );

  // ------------------------------------------------------------
  // Effect hooks
  // ------------------------------------------------------------

  // If deadman is triggered, reset menu index
  useEffect(() => {
    // When menu is not displayed, a blacklisted page has been loaded.
    // Check if deadman is triggered and reset menu index
    if (!display && params?.current?.deadman) {
      effectiveIndex.current = 0;
      setListIndex.current(0);
    }
  }, [display]);

  // ------------------------------------------------------------
  // Keyboard hook
  // ------------------------------------------------------------

  useKeyboard({
    focus: Focus.MENU,
    keyDownHandler: (key) => {
      switch (key) {
        case 'BACK':
          if (stackRef.current.length > 0) {
            dispatch(popRoute());
            return;
          }
          if (DeviceConfig.capabilities.exitFromMenu) {
            dispatch(setModal(Modals.EXIT_APP));
          }
          break;
        case 'RIGHT':
          loadAd(effectiveIndex.current);
          break;
        case 'SELECT':
          if (stackRef.current.length !== 0) {
            loadPage(effectiveIndex.current);
          }
          break;
      }
    },
  });

  // ------------------------------------------------------------
  // Memoized values
  // ------------------------------------------------------------

  return useMemo(() => {
    return {
      items,
      callbacks: {
        list: {
          onChange,
          onFocus,
          outOfBounds,
        },
        item: {
          onClick,
        },
      },
      props: {
        list: {
          direction: Direction.VERTICAL,
          focus: Focus.MENU,
          focused: focusedRef.current,
          initialIndex: Math.max(0, effectiveIndex.current),
          setListIndex,
        },
      },
    };
  }, [collapse, effectiveIndex, items, onChange, onFocus, outOfBounds]);
}
